import { useEffect, useState } from "react";
import styled from "styled-components";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import Link from "@mui/material/Link";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";
import { EntriesTable } from "@/app/utils/class/cms";
import { textSize, text_size } from "@/app/utils/MixinSize";
import NoInformation from "@/app/ui/Common/NoInformation";
import { getSearchParams, setRacesDetailLoading} from "@/app/utils/Common";

const StyledTableHeadCell = styled(TableCell)<{ $isMobile?: boolean }>(({ $isMobile }) => ({
    [`&.${tableCellClasses.root}`]: {
        ...textSize(11),
        fontWeight: 400,
        height: "32px",
        padding: $isMobile ? "8px 8px 8px 12px" : "4px 8px",
        lineHeight: "inherit"
    },
}));

const StyledTableHeadRow = styled(TableHead)(() => ({
    'th: first-child': {
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledTableCell = styled(TableCell)<{ $isMobile?: boolean, $isChinese?: boolean }>(({ $isMobile, $isChinese }) => ({
    [`&.${tableCellClasses.root}`]: {
        color: "#333333",
    },
    [`&.${tableCellClasses.body}`]: {
        ...textSize(13),
        fontWeight: 500,
        padding: $isMobile ? "8px 12px" : "4px 8px",
        verticalAlign: "baseline",
        textAlign: "left",
        height: "32px",
        width: $isChinese ? "25%" : "50%",
        lineHeight: "24px"
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(even)': {
        backgroundColor: "#F4F4F4",
    },
    '&:nth-of-type(odd)': {
        backgroundColor: "#FFFFFF",
    },
    'td, th': {
        border: 0,
    },
    'td:last-child, th:last-child': {
        paddingRight: "8px",
    },
    'td: first-child': {
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledNameDiv = styled('div')(() => ({
    display: "block",
    lineHeight: "17px"
}));

// Final Declaration
const StyledDeclDiv = styled('div')(() => ({
    height: "38px",
    padding: "10px 15px"
}));

const StyledLink = styled(Link)(() => ({
    [`&.MuiLink-root`]: {
        ...textSize(13),
        fontWeight: 500,
        color: "#122C68",
        textDecoration: "underline",
        "&:hover": {
            textDecoration: "underline",
            textDecorationColor: "#122C68"
        }
    }
}));

const NoAvailable = styled('div')`
    background: #FFF;
    padding: 60px 0;
    text-align: center;
    ${text_size(13)}
    font-weight: 500;
`;

const TabTitle = styled.div`
  width: 100%;
  height: 36px;
  line-height: 16px;
  padding: 10px 12px;
  ${text_size(15)}
  font-weight: 500;
  background-color: #EDEDED;
  box-shadow: 0px 2px 10px 0px rgb(0 0 0 / 10%);
`;

interface propType {
  setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const Entries: React.FC<propType> = (props: propType) => {
    const { state: lang } = useLanguage();
    const { t } = lang;
    const {state: myContextState} = useMyContext();
    const [entryRecords, setEntryRecords] = useState<EntriesTable | undefined>();
    const [declarationLink, setDeclarationLink] = useState<string | undefined>();
    const { b_cid } = getSearchParams(new URLSearchParams(window.location.search));

    useEffect(()=>{ 
        setEntryRecords(new EntriesTable(lang.langType, myContextState.selectedRace?.entries?.entriesTable));
        setDeclarationLink(myContextState.selectedMeeting?.finalDeclarationsAndBarrierDraws); 
         
        setTimeout(() => {
            setRacesDetailLoading(props.setRacesDetailLoading)
        }, 500);
    },[myContextState.selectedRace, myContextState.selectedMeeting])

    return (
        <>
            {props.type === "pc" ?
                (
                    <div>
                        {
                            entryRecords?.records?.length ?
                            <>
                                <TabTitle id="racingEntries">{t("title_entires")}</TabTitle>                            
                                <TableContainer sx={{ display: "flex", borderRadius: "0px 0px 4px 4px", boxShadow: "0px 2px 10px 0px #0000001A" }}>
                                    <Table id="racing_entries_pc_table" sx={{ width: "100%" }} aria-label="simple table">
                                        <StyledTableHeadRow id="racing_entries_pc_table_header">
                                            <tr>
                                            <StyledTableHeadCell colSpan={lang.langType === LANGTYPE_EN ? 1 : 2}>{entryRecords?.horseTitle}</StyledTableHeadCell>
                                            <StyledTableHeadCell colSpan={lang.langType === LANGTYPE_EN ? 1 : 2}>{entryRecords?.trainerTitle}</StyledTableHeadCell>
                                            </tr>
                                        </StyledTableHeadRow>
                                        <TableBody id="racing_entries_pc_table_body">
                                            {entryRecords?.records?.map((row: {
                                                    horse_en?: string | undefined;
                                                    horse_ch?: string | undefined;
                                                    trainer_en?: string | undefined;
                                                    trainer_ch?: string | undefined;
                                                }, idx: number) => (
                                                <StyledTableRow key={idx}>
                                                    <StyledTableCell $isChinese={lang.langType !== LANGTYPE_EN}>
                                                        {row.horse_en}
                                                    </StyledTableCell>
                                                    {
                                                        lang.langType === LANGTYPE_EN ? <></> :
                                                            <StyledTableCell $isChinese>
                                                                {row.horse_ch}
                                                            </StyledTableCell>
                                                    }
                                                    <StyledTableCell $isChinese={lang.langType !== LANGTYPE_EN}>
                                                        {row.trainer_en}
                                                    </StyledTableCell>
                                                    {
                                                        lang.langType === LANGTYPE_EN ? <></> :
                                                            <StyledTableCell $isChinese>
                                                                {row.trainer_ch}
                                                            </StyledTableCell>
                                                    }
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                                :
                                declarationLink ? (<>
                                  <TabTitle id="racingEntries">{t("title_entires")}</TabTitle>
                                    <NoAvailable>{t("entries_no_available")}</NoAvailable>
                                </>
                                ) 
                                :<>
                                 <TabTitle id="racingEntries">{t("title_entires")}</TabTitle>
                                <NoInformation></NoInformation></>
                               
                        } 
                        <StyledDeclDiv id="racing_entries_pc_link">
                            {
                                declarationLink && <StyledLink href={declarationLink}>{t("entries_declartionLink")}</StyledLink>
                            }
                        </StyledDeclDiv>
                    </div>
                ) : (
                    <>
                        <div style={{ boxShadow: "0px 2px 10px 0px #0000001A" }}>
                            {
                                entryRecords?.records?.length ?
                                    <TableContainer sx={{ display: "flex", borderRadius: "0px 0px 4px 4px" }}>
                                        <Table id="racing_entries_mobile_table" sx={{ width: "100%" }} aria-label="simple table">
                                            <StyledTableHeadRow id="racing_entries_mobile_table_header">
                                                <StyledTableHeadCell $isMobile>{entryRecords?.horseTitle}</StyledTableHeadCell>
                                                <StyledTableHeadCell $isMobile>{entryRecords?.trainerTitle}</StyledTableHeadCell>
                                            </StyledTableHeadRow>
                                            <TableBody id="racing_entries_mobile_table_body">
                                                {entryRecords?.records?.map((row: {
                                                    horse_en?: string | undefined;
                                                    horse_ch?: string | undefined;
                                                    trainer_en?: string | undefined;
                                                    trainer_ch?: string | undefined;
                                                }, idx: number) => (
                                                    <StyledTableRow key={idx}>
                                                        <StyledTableCell $isMobile>
                                                            <StyledNameDiv>{row.horse_en}</StyledNameDiv>
                                                            {
                                                                lang.langType === LANGTYPE_EN ? null : <StyledNameDiv>{row.horse_ch}</StyledNameDiv>
                                                            }
                                                        </StyledTableCell>
                                                        <StyledTableCell $isMobile>
                                                            <StyledNameDiv>{row.trainer_en}</StyledNameDiv>
                                                            {
                                                                lang.langType === LANGTYPE_EN ? null : <StyledNameDiv>{row.trainer_ch}</StyledNameDiv>
                                                            }
                                                        </StyledTableCell>

                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    :
                                    declarationLink ? (
                                        <NoAvailable>{t("entries_no_available")}</NoAvailable>
                                    ) 
                                    :
                                    <NoInformation></NoInformation>
                            }
                        </div>
                        <StyledDeclDiv id="racing_entries_mobile_link">
                            {
                                declarationLink && <StyledLink href={declarationLink}>{t("entries_declartionLink")}</StyledLink>
                            }
                        </StyledDeclDiv>
                    </>
                )
            }
        </>
    )
}

export default Entries; 